import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

export default function Calculator() {
  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1 flex flex-col">
        <div className="mx-auto max-w-content w-full px-4 md:px-6 pt-8 pb-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-600 text-background-50">
              <i className="ri-calculator-line text-xl" aria-hidden="true"></i>
            </span>
            <div>
              <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-3xl">
                Shot Allowance Calculator
              </h1>
              <p className="text-sm text-foreground-600 mt-0.5">
                Select your course, tee and format — get your playing handicap instantly
              </p>
            </div>
          </div>
        </div>
        <div className="flex-1 mx-auto max-w-content w-full px-0 md:px-6 pb-12">
          <div className="w-full rounded-lg overflow-hidden border border-background-200 bg-background-50" style={{ height: "calc(100vh - 200px)", minHeight: "700px" }}>
            <iframe
              src="https://app.handicalc.com"
              title="HANDICALC Shot Allowance Calculator"
              className="w-full h-full"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              loading="eager"
            />
          </div>
          <p className="text-xs text-foreground-500 text-center mt-4">
            Calculator powered by{" "}
            <a
              href="https://app.handicalc.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-700 hover:text-primary-800 underline cursor-pointer"
            >
              app.handicalc.com
            </a>
            {" "}— the full-featured HANDICALC® tool
          </p>
        </div>

        {/* SEO content section */}
        <section className="bg-background-100 border-y border-background-200 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              <div>
                <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">How it works</span>
                <h2 className="mt-3 font-heading font-bold text-foreground-950 text-2xl md:text-3xl leading-tight">
                  The fastest golf shot allowance calculator online
                </h2>
                <p className="mt-5 text-foreground-700 text-base leading-relaxed">
                  HANDICALC® is a free online golf shot allowance calculator that converts your
                  <strong> World Handicap System™ Handicap Index</strong> into a
                  <strong> Playing Handicap</strong> for any course, tee and format in seconds.
                  Simply search for your golf course, select the tee you are playing from,
                  choose your competition format, and enter your Handicap Index.
                  The calculator does the rest — applying the correct
                  <strong> Course Rating</strong>, <strong> Slope Rating</strong> and
                  <strong> WHS™ format allowance percentage</strong> automatically.
                </p>
                <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                  Whether you are preparing for a club medal, a society fourball betterball,
                  a foursomes match, or a charity scramble, HANDICALC® gives you the exact
                  number of strokes you receive before you step onto the first tee.
                </p>
              </div>
              <div className="space-y-4">
                <div className="rounded-xl bg-background-50 border border-background-200 p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-foreground-950 text-base mb-3">
                    Supported formats
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {["Singles stroke play", "Singles match play", "Fourball betterball", "Foursomes", "Greensomes", "Texas Scramble", "Pinehurst / Chapman", "Best 1–4 of 4", "Stableford", "Custom formats"].map((f) => (
                      <span key={f} className="inline-flex items-center px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium whitespace-nowrap">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl bg-background-50 border border-background-200 p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-foreground-950 text-base mb-3">
                    Key features
                  </h3>
                  <ul className="space-y-2 text-sm text-foreground-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Works with <strong>mixed tees</strong> — different tees for different players in the same group</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Handles <strong>mixed-gender competitions</strong> with separate Course Ratings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Covers <strong>thousands of rated courses across UK, Ireland &amp; Europe</strong> with official CR and SR data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Applies correct <strong>WHS™ allowance percentages</strong> for every format automatically</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Displays <strong>per-hole stroke allocation</strong> based on the course Stroke Index</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-xl bg-background-50 border border-background-200 p-6 md:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-search-line text-xl" aria-hidden="true"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-base">Step 1: Search your course</h3>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                  Type the name of your golf course into the search bar. HANDICALC® will show matching courses from its database of thousands of rated courses across the UK, Ireland and Europe. Select the correct course and tee.
                </p>
              </div>
              <div className="rounded-xl bg-background-50 border border-background-200 p-6 md:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mb-4">
                  <i className="ri-equalizer-line text-xl" aria-hidden="true"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-base">Step 2: Choose format & enter HI</h3>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                  Select your playing format from the dropdown — singles, fourball, foursomes, scramble and more. Then enter each player's Handicap Index. HANDICALC® supports plus handicaps too (enter as negative numbers).
                </p>
              </div>
              <div className="rounded-xl bg-background-50 border border-background-200 p-6 md:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 mb-4">
                  <i className="ri-shield-check-line text-xl" aria-hidden="true"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-base">Step 3: Get instant results</h3>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                  Your Playing Handicap appears instantly, along with per-hole shot allowances. Save, share or edit the calculation. Results are accurate to official WHS™ methodology.
                </p>
              </div>
            </div>

            <div className="mt-12 rounded-xl bg-primary-50 border border-primary-200/50 p-6 md:p-8">
              <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-4">
                Frequently asked calculator questions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <details className="group">
                  <summary className="cursor-pointer text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                    <span>Do I need to create an account?</span>
                    <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                  </summary>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                    No. HANDICALC® works without registration. Just open the calculator, search your course and get your result. No login, no email required.
                  </p>
                </details>
                <details className="group">
                  <summary className="cursor-pointer text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                    <span>Is this an official WHS™ calculator?</span>
                    <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                  </summary>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                    HANDICALC® applies published World Handicap System™ methodology principles to calculate Course Handicap and Playing Handicap. It is an independent tool, not endorsed by the R&A or USGA, but follows the same calculation logic.
                  </p>
                </details>
                <details className="group">
                  <summary className="cursor-pointer text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                    <span>Can I calculate for multiple players?</span>
                    <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                  </summary>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                    Yes. Enter up to four players with their individual Handicap Indexes. Each player's Course Handicap and Playing Handicap is calculated separately. Perfect for fourball, foursomes, greensomes and scramble formats.
                  </p>
                </details>
                <details className="group">
                  <summary className="cursor-pointer text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                    <span>What if my course isn't listed?</span>
                    <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                  </summary>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                    HANDICALC® covers thousands of rated courses across the UK, Ireland and Europe. If your course is missing, check the spelling or try a shorter search term. Contact us to request a course addition.
                  </p>
                </details>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}